import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { Route, Routes, useParams } from 'react-router-dom'
import LoadingSpinner from './app/components/common/LoadingSpinner'
import BackendOnlineGuard from './app/guards/BackendOnline'
import PrivateRoute from './app/guards/PrivateRoute'
import PublicRoute from './app/guards/PublicRoute'
import ForgotPassword from './app/modules/forgot-password/ForgotPassword'
import Login from './app/modules/login/Login'
import Main from './app/modules/main/Main'
import RecoverPassword from './app/modules/recover-password/RecoverPassword'
import { Dashboard } from './app/pages/dashboard/Dashboard'
import { getUtiliteitsnet } from './app/services/grondwerk.service'
import './App.css'
import './app/util/dymo' // Register test commands

// Lazy loaded components
const BagAddresses = React.lazy(() => import('./app/pages/bag/Adressen'))
const BagAddress = React.lazy(() => import('./app/pages/bag/[id]/Adres'))
const RevokedBagDashboard = React.lazy(() => import('./app/pages/bag/revoked/RevokedBagDashboard'))
const Projects = React.lazy(() => import('./app/pages/projecten/page'))
const Customers = React.lazy(() => import('./app/pages/customers/Customers'))
const Customer = React.lazy(() => import('./app/pages/customers/[id]/Customer'))
const ProjectToevoegen = React.lazy(() => import('./app/pages/projecten/Toevoegen').then(module => ({ default: module.ProjectToevoegen })))
const RouterProvisioning = React.lazy(() => import('./app/pages/provisioning/router/RouterProvisioning'))
const RouterProvisioningToevoegen = React.lazy(() => import('./app/pages/provisioning/router/Toevoegen'))
const Router = React.lazy(() => import('./app/pages/provisioning/router/[id]/Router').then(module => ({ default: module.Router })))
const ProjectModule = React.lazy(() => import('./app/pages/projecten/[id]/ProjectModule').then(module => ({ default: module.ProjectModule })))
const VialerCsvProcessor = React.lazy(() => import('./app/pages/tools/vialercsvprocessor').then(module => ({ default: module.VialerCsvProcessor })))
const ImportJob = React.lazy(() => import('./app/pages/bag/import/[id]/ImportJob').then(module => ({ default: module.ImportJob })))
const ImportJobs = React.lazy(() => import('./app/pages/bag/import/Imports').then(module => ({ default: module.ImportJobs })))
const CustomerLostAndFound = React.lazy(() => import('./app/pages/customers/LostAndFound').then(module => ({ default: module.CustomerLostAndFound })))
const GponPreProcessor = React.lazy(() => import('./app/pages/tools/gponPreProcessor').then(module => ({ default: module.GponPreProcessor })))
const ZipCheckLogs = React.lazy(() => import('./app/pages/tools/ZipCheckLogs').then(module => ({ default: module.ZipCheckLogsPage })))
const VoipCustomers = React.lazy(() => import('./app/pages/voip/klanten/VoipKlanten').then(module => ({ default: module.VoipCustomers })))
const VoipCustomer = React.lazy(() => import('./app/pages/voip/klanten/[id]/VoipKlant').then(module => ({ default: module.VoipCustomer })))
const VoipToestel = React.lazy(() => import('./app/pages/voip/klanten/[id]/toestellen/[id]/VoipToestel').then(module => ({ default: module.VoipToestel })))
const SettingsModule = React.lazy(() => import('./app/pages/settings/SettingsModule').then(module => ({ default: module.SettingsModule })))
const BufinderList = React.lazy(() => import('./app/pages/bag/bufinder/BufinderList').then(module => ({ default: module.BufinderList })))
const BufinderProject = React.lazy(() => import('./app/pages/bag/bufinder/[id]/BufinderProject').then(module => ({ default: module.BufinderProject })))
const NetwerkbeheerModule = React.lazy(() => import('./app/pages/netwerkbeheer/Netwerkbeheer.module').then(module => ({ default: module.NetwerkbeheerModule })))
const MeldingenModule = React.lazy(() => import('./app/pages/meldingen/meldingenModule').then(module => ({ default: module.MeldingenModule })))
const InventoryModule = React.lazy(() => import('./app/pages/inventory/InventoryModule').then(module => ({ default: module.InventoryModule })))
const AdministratieModule = React.lazy(() => import('./app/pages/administratie/AdministratieModule').then(module => ({ default: module.AdministratieModule })))
const PropositionsDashboard = React.lazy(() => import('./app/pages/propositions/Dashboard').then(module => ({ default: module.PropositionsDashboard })))
const PropositionDetail = React.lazy(() => import('./app/pages/propositions/Detail').then(module => ({ default: module.PropositionDetail })))
const PropositionAssignments = React.lazy(() => import('./app/pages/propositions/Assignments').then(module => ({ default: module.PropositionAssignments })))
const WorkflowsPage = React.lazy(() => import('./app/pages/workflows/Workflows').then(module => ({ default: module.WorkflowsPage })))

// Grondwerk module
const NettenListPage = React.lazy(() => import('./app/pages/grondwerk/NettenList').then(module => ({ default: module.NettenList })))
const GrondwerkLoaderPage = React.lazy(() => import('./app/pages/grondwerk/GrondwerkLoader').then(module => ({ default: module.GrondwerkLoader })))

// KLIC (Kadaster) integration
const KlicSettingsPage = React.lazy(() => import('./app/pages/klic/KlicSettings').then(module => ({ default: module.KlicSettings })))
const KadasterCallbackPage = React.lazy(() => import('./app/pages/klic/KadasterCallback').then(module => ({ default: module.KadasterCallback })))

// RADIUS Module
const NASPage = React.lazy(() => import('./app/pages/radius/NAS').then(module => ({ default: module.NASPage })))
const AuthenticationLogPage = React.lazy(() => import('./app/pages/radius/AuthenticationLog').then(module => ({ default: module.AuthenticationLogPage })))
const ActiveSessionsPage = React.lazy(() => import('./app/pages/radius/ActiveSessions').then(module => ({ default: module.ActiveSessionsPage })))
const SessionHistoryPage = React.lazy(() => import('./app/pages/radius/SessionHistory').then(module => ({ default: module.SessionHistoryPage })))
const SessionsDashboard = React.lazy(() => import('./app/pages/radius/SessionsDashboard').then(module => ({ default: module.SessionsDashboard })))
const RadiusDashboard = React.lazy(() => import('./app/pages/radius/RadiusDashboard').then(module => ({ default: module.RadiusDashboard })))
const UserLookup = React.lazy(() => import('./app/pages/radius/UserLookup').then(module => ({ default: module.UserLookup })))
const Reports = React.lazy(() => import('./app/pages/radius/Reports').then(module => ({ default: module.Reports })))
const OrphanedRecordsPage = React.lazy(() => import('./app/pages/radius/OrphanedRecords').then(module => ({ default: module.OrphanedRecordsPage })))

/** Route wrapper: reads :id param and passes netId + netNaam into GrondwerkLoader */
function GrondwerkNetEditorRoute() {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading } = useQuery({
    queryKey: ['grondwerk', 'netten', id],
    queryFn: () => getUtiliteitsnet(id!),
    enabled: !!id,
  })
  if (isLoading || !data)
    return <LoadingSpinner message="Net laden..." />
  return (
    <React.Suspense fallback={<LoadingSpinner message="Editor laden..." size="large" />}>
      <GrondwerkLoaderPage netId={data.id} netNaam={data.naam} />
    </React.Suspense>
  )
}

function App(): React.JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute />}>
        <Route path="/login" element={<Login />} />
      </Route>
      <Route path="/forgot-password" element={<PublicRoute />}>
        <Route path="/forgot-password" element={<ForgotPassword />} />
      </Route>
      <Route path="/recover-password" element={<PublicRoute />}>
        <Route path="/recover-password" element={<RecoverPassword />} />
      </Route>
      <Route path="/" element={<BackendOnlineGuard />}>
        <Route path="/" element={<PrivateRoute />}>
          <Route path="/" element={<Main />}>
            {/* KLIC (Kadaster) integration */}
            <Route
              path="/oauth/kadaster/callback"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="KLIC-koppeling voltooien..." />}>
                  <KadasterCallbackPage />
                </React.Suspense>
              )}
            />
            <Route
              path="/settings/klic"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="KLIC-instellingen laden..." />}>
                  <KlicSettingsPage />
                </React.Suspense>
              )}
            />
            {/* Dedicated graafmeldingen map (deferred Mapbox init via the loader) */}
            <Route
              path="/klic/graafmeldingen"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Graafmeldingen-kaart laden..." size="large" />}>
                  {React.createElement(React.lazy(() => import('./app/pages/klic/KlicMapLoader')))}
                </React.Suspense>
              )}
            />
            {/* Module BAG */}
            <Route
              path="/bag/adressen/:id"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading address details..." />}>
                  <BagAddress />
                </React.Suspense>
              )}
            />
            <Route
              path="/bag/adressen"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading addresses..." />}>
                  <BagAddresses />
                </React.Suspense>
              )}
            />
            <Route
              path="/bag/import/:id"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading import job..." />}>
                  <ImportJob />
                </React.Suspense>
              )}
            />
            <Route
              path="/bag/import"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading import jobs..." />}>
                  <ImportJobs />
                </React.Suspense>
              )}
            />
            <Route
              path="/bag/bufinder/:id"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading project details..." />}>
                  <BufinderProject />
                </React.Suspense>
              )}
            />
            <Route
              path="/bag/bufinder"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading business unit finder..." />}>
                  <BufinderList />
                </React.Suspense>
              )}
            />
            <Route
              path="/bag/revoked"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading revoked addresses..." />}>
                  <RevokedBagDashboard />
                </React.Suspense>
              )}
            />

            {/* Module Netwerkbeheer */}
            <Route
              path="/netwerkbeheer/*"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading network management..." />}>
                  <NetwerkbeheerModule />
                </React.Suspense>
              )}
            />

            {/* Module Meldingen */}
            <Route
              path="/meldingen/*"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading notifications..." />}>
                  <MeldingenModule />
                </React.Suspense>
              )}
            />

            {/* Module VOIP */}
            <Route
              path="/voip/klanten/:accountid/toestellen/:macaddress"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading VoIP device..." />}>
                  <VoipToestel />
                </React.Suspense>
              )}
            />
            <Route
              path="/voip/klanten/:id"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading VoIP customer..." />}>
                  <VoipCustomer />
                </React.Suspense>
              )}
            />
            <Route
              path="/voip/klanten"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading VoIP customers..." />}>
                  <VoipCustomers />
                </React.Suspense>
              )}
            />

            {/* Module Customers (zoho) */}
            <Route
              path="/customers/lostAndFound"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading lost customers..." />}>
                  <CustomerLostAndFound />
                </React.Suspense>
              )}
            />
            <Route
              path="/customers/:id"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading customer details..." />}>
                  <Customer />
                </React.Suspense>
              )}
            />
            <Route
              path="/customers"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading customers..." />}>
                  <Customers />
                </React.Suspense>
              )}
            />

            {/* Module Projects */}
            <Route
              path="/projecten"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading projects..." />}>
                  <Projects />
                </React.Suspense>
              )}
            />
            <Route
              path="/projecten/toevoegen"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading project form..." />}>
                  <ProjectToevoegen />
                </React.Suspense>
              )}
            />
            <Route
              path="/projecten/:id/*"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading project..." />}>
                  <ProjectModule />
                </React.Suspense>
              )}
            />

            {/* Module Provisioning */}
            <Route
              path="/provisioning/routers"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading router provisioning..." />}>
                  <RouterProvisioning />
                </React.Suspense>
              )}
            />
            <Route
              path="/provisioning/routers/toevoegen"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading router form..." />}>
                  <RouterProvisioningToevoegen />
                </React.Suspense>
              )}
            />
            <Route
              path="/provisioning/routers/:id"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading router details..." />}>
                  <Router />
                </React.Suspense>
              )}
            />

            {/* Module Voorraadbeheer */}
            <Route
              path="/voorraad/*"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading inventory..." />}>
                  <InventoryModule />
                </React.Suspense>
              )}
            />

            {/* Module Administratie */}
            <Route
              path="/administratie/*"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading administration..." />}>
                  <AdministratieModule />
                </React.Suspense>
              )}
            />

            {/* Module Propositions */}
            <Route
              path="/propositions"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading propositions..." />}>
                  <PropositionsDashboard />
                </React.Suspense>
              )}
            />
            <Route
              path="/propositions/assignments"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading assignments..." />}>
                  <PropositionAssignments />
                </React.Suspense>
              )}
            />
            <Route
              path="/propositions/:id"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading proposition details..." />}>
                  <PropositionDetail />
                </React.Suspense>
              )}
            />

            {/* Module RADIUS */}
            <Route
              path="/radius"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading RADIUS dashboard..." />}>
                  <RadiusDashboard />
                </React.Suspense>
              )}
            />
            <Route
              path="/radius/dashboard"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading RADIUS dashboard..." />}>
                  <RadiusDashboard />
                </React.Suspense>
              )}
            />
            <Route
              path="/radius/sessions/dashboard"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading sessions dashboard..." />}>
                  <SessionsDashboard />
                </React.Suspense>
              )}
            />
            <Route
              path="/radius/nas"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading NAS management..." />}>
                  <NASPage />
                </React.Suspense>
              )}
            />
            <Route
              path="/radius/auth-logs"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading authentication logs..." />}>
                  <AuthenticationLogPage />
                </React.Suspense>
              )}
            />
            <Route
              path="/radius/sessions/active"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading active sessions..." />}>
                  <ActiveSessionsPage />
                </React.Suspense>
              )}
            />
            <Route
              path="/radius/sessions/history"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading session history..." />}>
                  <SessionHistoryPage />
                </React.Suspense>
              )}
            />
            <Route
              path="/radius/user-lookup"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading user lookup..." />}>
                  <UserLookup />
                </React.Suspense>
              )}
            />
            <Route
              path="/radius/reports"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading reports..." />}>
                  <Reports />
                </React.Suspense>
              )}
            />
            <Route
              path="/radius/maintenance/orphaned"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading orphaned records..." />}>
                  <OrphanedRecordsPage />
                </React.Suspense>
              )}
            />

            {/* Module Tools */}
            <Route
              path="/tools/belkosten2exact"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading Vialer CSV processor..." />}>
                  <VialerCsvProcessor />
                </React.Suspense>
              )}
            />
            <Route
              path="/tools/gponpreprocessor"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading GPON preprocessor..." />}>
                  <GponPreProcessor />
                </React.Suspense>
              )}
            />
            <Route
              path="/tools/zipchecklogs"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading Zip Check Logs..." />}>
                  <ZipCheckLogs />
                </React.Suspense>
              )}
            />

            {/* Module Grondwerk */}
            <Route
              path="/grondwerk/netten/:id"
              element={<GrondwerkNetEditorRoute />}
            />
            <Route
              path="/grondwerk"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Grondwerk laden..." />}>
                  <NettenListPage />
                </React.Suspense>
              )}
            />

            {/* Module Map */}
            {/* Use MapLoader to prevent Mapbox initialization until needed */}
            <Route
              path="/kaart"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading map..." size="large" />}>
                  {React.createElement(React.lazy(() => import('./app/pages/map/MapLoader')))}
                </React.Suspense>
              )}
            />

            {/* Module Settings */}
            <Route
              path="/settings/*"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading settings..." />}>
                  <SettingsModule />
                </React.Suspense>
              )}
            />

            {/* Workflows */}
            <Route
              path="/workflows"
              element={(
                <React.Suspense fallback={<LoadingSpinner message="Loading workflows..." />}>
                  <WorkflowsPage />
                </React.Suspense>
              )}
            />

            {/* Module Dashboard */}

            <Route path="/" element={<Dashboard />} />

            <Route path="test/" element={<div>test</div>} />
            <Route path="*" element={<div>Not found</div>} />
          </Route>
        </Route>
      </Route>
    </Routes>
  )
}

export default App
